import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";

type Item = {
  id: string;
  title: string;
  publish_date: string;
  status: string;
  cluster: string | null;
};

const ACTIVE_STATUSES = ["idea", "draft", "scheduled"] as const;

export const AdminReminderBell = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const today = new Date();
      const in7 = new Date();
      in7.setDate(today.getDate() + 7);
      const { data } = await supabase
        .from("content_calendar_items")
        .select("id,title,publish_date,status,cluster")
        .in("status", ACTIVE_STATUSES)
        .lte("publish_date", in7.toISOString().slice(0, 10))
        .order("publish_date", { ascending: true });
      if (alive) setItems((data as Item[]) || []);
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const overdue = items.filter((i) => i.publish_date < today);
  const upcoming = items.filter((i) => i.publish_date >= today);
  const count = items.length;

  const fmt = (d: string) => {
    const dt = new Date(d + "T00:00:00");
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative hidden sm:inline-flex" title="Content reminders">
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] leading-none flex items-center justify-center"
            >
              {count > 9 ? "9+" : count}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3 border-b">
          <div className="font-semibold text-sm">Content calendar reminders</div>
          <div className="text-xs text-muted-foreground">Next 7 days & overdue</div>
        </div>
        <div className="max-h-96 overflow-auto">
          {count === 0 && (
            <div className="p-4 text-sm text-muted-foreground text-center">Nothing due. Nice.</div>
          )}
          {overdue.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-destructive font-semibold bg-destructive/5">
                Overdue ({overdue.length})
              </div>
              {overdue.map((i) => (
                <Row key={i.id} item={i} fmt={fmt} danger onClick={() => setOpen(false)} />
              ))}
            </div>
          )}
          {upcoming.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold bg-muted/40">
                Upcoming ({upcoming.length})
              </div>
              {upcoming.map((i) => (
                <Row key={i.id} item={i} fmt={fmt} onClick={() => setOpen(false)} />
              ))}
            </div>
          )}
        </div>
        <div className="p-2 border-t">
          <Link to="/admin" onClick={() => setOpen(false)}>
            <Button variant="ghost" size="sm" className="w-full justify-center text-xs">
              Open calendar
            </Button>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Row = ({
  item,
  fmt,
  danger,
  onClick,
}: {
  item: Item;
  fmt: (d: string) => string;
  danger?: boolean;
  onClick: () => void;
}) => (
  <Link
    to="/admin"
    onClick={onClick}
    className="block px-3 py-2 hover:bg-muted/50 border-b last:border-b-0"
  >
    <div className="flex items-start justify-between gap-2">
      <div className="text-sm font-medium line-clamp-2">{item.title}</div>
      <div className={`text-[11px] whitespace-nowrap ${danger ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
        {fmt(item.publish_date)}
      </div>
    </div>
    <div className="flex items-center gap-2 mt-0.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{item.status}</span>
      {item.cluster && (
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">· {item.cluster}</span>
      )}
    </div>
  </Link>
);
